// src/components/CarFilterBar.tsx

import {
  Box,
  Button,
  Grid,
  TextField,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  Typography,
  Slider,
  Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import SortIcon from '@mui/icons-material/Sort';
import ViewModuleIcon from '@mui/icons-material/ViewModule'; // Grid icon
import ViewListIcon from '@mui/icons-material/ViewList';     // List icon
import ClearIcon from '@mui/icons-material/Clear';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import RightDrawer from './RightDrawer';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { RangeFilterMenu } from './RangeFilterMenu';
import { enqueueSnackbar } from 'notistack';
import axiosInstance from '../services/axiosInstance';





type CarFilterBarProps = {
  viewMode?: 'grid' | 'list';
  onToggleViewMode?: () => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  url?: string;
  // ✅ New prop to update filters
  onFilterChange: (key: string, value: string) => void;
};



const CarFilterBar = ({
  viewMode,
  onToggleViewMode,
  searchQuery,
  onSearchChange,
  url,
}: CarFilterBarProps) => {
  const navigate = useNavigate();

  const [dealerData, setDealerData] = useState<any[]>([]);
  const [fuelTypeData, setvehicleFuelTypeData] = useState<any[]>([]);
  const [TransmissionTypeData, setvehicleTransmissionTypeData] = useState<any[]>([]);
  const [bodyTypesData, setvehicleBodyTypeData] = useState<any[]>([]);
  const [brandData, setvehicleBrandData] = useState<any[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const [initializedKeys, setInitializedKeys] = useState<Set<string>>(new Set());



  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  type SortItem = {
    key: string;
    direction: 'asc' | 'desc';
  };

  const [sortList, setSortList] = useState<SortItem[]>([]);


  const handleSortClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };



  const handleSortChange = (key: string) => {
    setSortList((prev) => {
      const existing = prev.find((s) => s.key === key);

      let updated: SortItem[];

      if (existing) {
        // Toggle direction or remove if DESC → ASC → remove
        const newDirection = existing.direction === 'asc' ? 'desc' : 'asc';
        updated = prev.map((s) =>
          s.key === key ? { ...s, direction: newDirection } : s
        );
      } else {
        // Add new sort key to the end
        updated = [...prev, { key, direction: 'asc' }];
      }

      const params = new URLSearchParams(location.search);
      params.set('sortKeys', updated.map((s) => s.key).join(','));
      params.set('sortDirs', updated.map((s) => s.direction).join(','));
      params.delete('page');

      navigate(`${url ?? '/vehicle-listing'}?${params.toString()}`);
      navigate(`${url ?? '/vehicle-listing'}?${params.toString()}`);


      return updated;
    });
  };

  const sortLabelMap: Record<string, string> = {
    askingPrice: 'Price',
    addDate: 'Added Date',
    mileage: 'Mileage',
    vehicleModel: 'Model',
  };




  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 15;
  const maxYear = currentYear;

  const yearMarks = Array.from({ length: 6 }, (_, i) => {
    const year = minYear + i * 3;
    return { value: year, label: year.toString() };
  });

  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const keys = params.get('sortKeys')?.split(',') || [];
    const dirs = params.get('sortDirs')?.split(',') || [];

    if (keys.length === dirs.length && keys.length > 0) {
      const restoredSortList: SortItem[] = keys.map((key, index) => ({
        key,
        direction: (dirs[index] === 'desc' ? 'desc' : 'asc') as 'asc' | 'desc',
      }));

      setSortList(restoredSortList);
    }
  }, [location.search]);


  const searchParams = new URLSearchParams(location.search);

  const [yearRange, setYearRange] = useState<number[]>([
    Number(searchParams.get('minYear')) || minYear,
    Number(searchParams.get('maxYear')) || maxYear,
  ]);

  const handleYearChange = (_: any, newValue: number | number[]) => {
    setYearRange(newValue as number[]);
  };

  const applyYearFilter = () => {
    const [minSelected, maxSelected] = yearRange;
    const currentParams = new URLSearchParams(location.search);
    currentParams.set('minManufacturingYear', minSelected.toString());
    currentParams.set('maxManufacturingYear', maxSelected.toString());
    currentParams.delete('page');
    navigate(`${url ?? '/vehicle-listing'}?${currentParams.toString()}`);
    handleClose();
  };

  const clearYearFilter = () => {
    const currentParams = new URLSearchParams(location.search);
    currentParams.delete('minManufacturingYear');
    currentParams.delete('maxManufacturingYear');
    navigate(`${url ?? '/vehicle-listing'}?${currentParams.toString()}`);
    setYearRange([minYear, maxYear]);
    handleClose();
  };



  useEffect(() => {
    const fetchData = async () => {
      try {
        const payload = {
          dealerCompanyData: true,
          vehicleBodyTypeData: true,
          vehicleFuelTypeData: true,
          vehicleTransmissionTypeData: true,
          vehicleBrandData: true,
        };

        const response = await axiosInstance.post(
          "/CustomerVehicle/VehicleMultipleDataList",
          payload,
          { headers: { "Content-Type": "application/json" } }
        );

        const result = response.data as {
          responseCode: string;
          responseMessage?: string;
          responseData?: {
            dealerCompanyData?: any[];
            vehicleBodyTypeData?: any[];
            vehicleFuelTypeData?: any[];
            vehicleTransmissionTypeData?: any[];
            vehicleBrandData?: any[];
          };
        };

        if (result.responseCode === "00" && result.responseData) {
          setvehicleBodyTypeData(result.responseData.vehicleBodyTypeData || []);
          setvehicleFuelTypeData(result.responseData.vehicleFuelTypeData || []);
          setvehicleTransmissionTypeData(result.responseData.vehicleTransmissionTypeData || []);
          setvehicleBrandData(result.responseData.vehicleBrandData || []);
          setDealerData(result.responseData.dealerCompanyData || []);
        } else {
          enqueueSnackbar(result.responseMessage || "Failed to fetch vehicle data", {
            variant: "error",
          });
        }
      } catch (error) {
        enqueueSnackbar("Something went wrong while fetching vehicle data", {
          variant: "error",
        });
      }
    };

    fetchData();
  }, []);


  const filterButtons = [
    'Brand & Model',
    'Body Type',
    'Fuel Type',
    'Vehicle Status',
    'Depreciation',
    'Car Price & Instalment',
    'Year',
    'Transmission',
    'Mileage',
  ];

  const filterMap: Record<
    string,
    {
      key: string;
      data?: { [key: string]: any; description: string }[]; // optional for price/year/mileage
      idField?: string;
      getLabel?: (searchParams: URLSearchParams) => string | null;
    }
  > = {
    'Body Type': {
      key: 'vehicleBodyTypeIds',
      data: bodyTypesData,
      idField: 'vehicleBodyTypeId',
    },
    'Brand & Model': {
      key: 'vehicleBrandIds',
      data: brandData,
      idField: 'vehicleBrandId',
    },
    'Fuel Type': {
      key: 'vehicleFuelTypeIds',
      data: fuelTypeData,
      idField: 'vehicleFuelTypeId',
    },
    Transmission: {
      key: 'vehicleTransmissionTypeIds',
      data: TransmissionTypeData,
      idField: 'vehicleTransmissionTypeId',
    },
    'Car Price & Instalment': {
      key: 'minAskingPrice', // used just to indicate filtering
      getLabel: (searchParams: URLSearchParams) => {
        const min = searchParams.get('minAskingPrice');
        const max = searchParams.get('maxAskingPrice');
        if (!min && !max) return null;

        const minVal = min ? Number(min) : null;
        const maxVal = max ? Number(max) : null;

        if (minVal !== null && maxVal !== null) {
          return `$${minVal.toLocaleString()} - $${maxVal.toLocaleString()}`;
        } else if (minVal !== null) {
          return `Above $${minVal.toLocaleString()}`;
        } else if (maxVal !== null) {
          return `Under $${maxVal.toLocaleString()}`;
        }

        return null;
      },
    },
    'Vehicle Status': {
      key: 'statusId',
      data: [
        { statusId: 'VPB', description: 'Available' },
        { statusId: 'VSL', description: 'Sold' },
      ],
      idField: 'statusId',
    },
    Year: {
      key: 'minManufacturingYear',
      getLabel: (searchParams: URLSearchParams) => {
        const minYear = searchParams.get('minManufacturingYear');
        const maxYear = searchParams.get('maxManufacturingYear');

        if (!minYear && !maxYear) return null;

        if (minYear && maxYear) return `${minYear} - ${maxYear}`;
        if (minYear) return `From ${minYear}`;
        if (maxYear) return `Before ${maxYear}`;

        return null;
      },
    },
    Mileage: {
      key: 'minMileage',
      getLabel: (searchParams: URLSearchParams) => {
        const minMileage = searchParams.get('minMileage');
        const maxMileage = searchParams.get('maxMileage');

        if (!minMileage && !maxMileage) return null;

        const formatKm = (km: string) =>
          `${Number(km).toLocaleString()} km`;

        if (minMileage && maxMileage) return `${formatKm(minMileage)} - ${formatKm(maxMileage)}`;
        if (minMileage) return `Above ${formatKm(minMileage)}`;
        if (maxMileage) return `Under ${formatKm(maxMileage)}`;

        return null;
      },
    },
    Depreciation: {
      key: 'minDepreciation',
      getLabel: (searchParams: URLSearchParams) => {
        const minDep = searchParams.get('minDepreciation');
        const maxDep = searchParams.get('maxDepreciation');

        if (!minDep && !maxDep) return null;

        const formatCurrency = (amount: string) =>
          `$${Number(amount).toLocaleString()}`;

        if (minDep && maxDep) return `${formatCurrency(minDep)} - ${formatCurrency(maxDep)}`;
        if (minDep) return `Above ${formatCurrency(minDep)}`;
        if (maxDep) return `Under ${formatCurrency(maxDep)}`;

        return null;
      },
    },

  };


  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>, filter: string) => {
    event.stopPropagation();
    setSelectedLabel(filter); // mark as selected
    setAnchorEl(event.currentTarget);
    setActiveFilter(filter);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setActiveFilter(null);
  };

  // At the top of your component:
  const [tempSelected, setTempSelected] = useState<{ [key: string]: string[] }>({});

  // Helper: initialize if not present
  const getTempSelected = (key: string): string[] => {
    // Initialize temp state from URL if not done before
    if (!initializedKeys.has(key)) {
      const searchParams = new URLSearchParams(location.search);
      const fromUrl = searchParams.get(key);
      const values = fromUrl ? fromUrl.split(',') : [];
      setTempSelected((prev) => ({ ...prev, [key]: values }));
      setInitializedKeys((prev) => new Set(prev).add(key));
      return values;
    }

    return tempSelected[key] || [];
  };



  const toggleSelection = (filterKey: string, id: string) => {
    setTempSelected((prev) => {
      const current = new Set(prev[filterKey] || []);
      if (current.has(id)) current.delete(id);
      else current.add(id);
      return { ...prev, [filterKey]: Array.from(current) };
    });
  };


  const applySelection = (filterKey: string) => {
    const currentParams = new URLSearchParams(location.search);
    const values = tempSelected[filterKey] || [];

    if (values.length > 0) {
      currentParams.set(filterKey, values.join(','));
    } else {
      currentParams.delete(filterKey);
    }

    currentParams.delete('page');
    navigate(`${url ?? '/vehicle-listing'}?${currentParams.toString()}`);
    handleClose();

    // Keep temp state for re-opening
    // ✅ Don't clear tempSelected here
  };



  const clearSelection = (filterKey: string) => {
    setTempSelected((prev) => ({ ...prev, [filterKey]: [] }));
    setInitializedKeys((prev) => {
      const updated = new Set(prev);
      updated.delete(filterKey);
      return updated;
    });

    const currentParams = new URLSearchParams(location.search);
    currentParams.delete(filterKey);
    currentParams.delete('page');

    navigate(`${url ?? '/vehicle-listing'}?${currentParams.toString()}`);
    handleClose();
  };



  const renderCheckboxFilterMenu = (
    key: string,
    data: any[],
    idField: string,
    labelField: string
  ) => (
    <Box sx={{ width: 220, maxHeight: 300, display: 'flex', flexDirection: 'column' }}>
      {/* Scrollable options */}
      <Box sx={{ flex: 1, px: 1, py: 1 }}>
        {data.map((item) => {
          const idStr = item[idField].toString();
          const isChecked = getTempSelected(key).includes(idStr);

          return (
            <MenuItem
              key={idStr}
              disableRipple
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              onClick={() => toggleSelection(key, idStr)}
            >
              <input type="checkbox" checked={isChecked} readOnly />
              <Typography>{item[labelField]}</Typography>
            </MenuItem>
          );
        })}
      </Box>

      {/* Always-visible buttons */}
      <Box
        sx={{
          borderTop: '1px solid #eee',
          p: 1,
          bgcolor: '#fff',
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
        }}
      >
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" onClick={() => clearSelection(key)}>
            Clear
          </Button>
          <Button size="small" variant="contained" onClick={() => applySelection(key)}>
            Apply
          </Button>
        </Stack>
      </Box>
    </Box>
  );



  return (
    <>
      <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderRadius: 2, height: 'auto' }}>
        <Grid container spacing={1} alignItems="center">
          <Grid size={{ xs: 12, md: 9 }}>
            <TextField
              fullWidth
              placeholder="Search for cars by Brand, Model, or Keywords"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1 }} />,
                endAdornment: searchQuery && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      onSearchChange('');
                      const params = new URLSearchParams(location.search);
                      params.delete('vehicleModel');
                      params.delete('search');
                      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ),
                sx: { backgroundColor: '#f4f6f8', borderRadius: 1 },
              }}
            />
          </Grid>

          <Grid size={{ xs: 6, md: 1.5 }}>
            <Button
              variant="contained"
              size="small"
              fullWidth
              onClick={() => {
                const params = new URLSearchParams(location.search);
                if (searchQuery) {
                  params.set('vehicleModel', searchQuery);
                } else {
                  params.delete('vehicleModel');
                }
                navigate(`${location.pathname}?${params.toString()}`);
              }}
            >
              Search
            </Button>
          </Grid>

          <Grid size={{ xs: 6, md: 1.5 }}>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              color="error"
              startIcon={<RestartAltIcon fontSize="small" />}
              onClick={() => {
                onSearchChange('');
                const params = new URLSearchParams(location.search);
                params.delete('vehicleModel');
                params.delete('search');
                navigate(`${location.pathname}?${params.toString()}`, { replace: true });
              }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>



        <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
          {filterButtons.map((label) => {
            const filterConfig = filterMap[label];

            let displayLabel = label;
            let subLabel: string | null = null;
            let variant: 'outlined' | 'contained' = 'outlined';

            if (filterConfig) {
              const searchParams = new URLSearchParams(location.search);

              // For filters like price that use getLabel()
              if (filterConfig.getLabel) {
                const resultLabel = filterConfig.getLabel(searchParams);
                if (resultLabel) {
                  subLabel = resultLabel;
                  variant = 'contained';
                }
              } else {
                const selectedIds = searchParams.get(filterConfig.key)?.split(',') || [];
                const selectedCount = selectedIds.length;

                if (selectedCount > 0 && filterConfig.data && filterConfig.idField) {
                  const firstSelected = filterConfig.data.find(
                    (item) => item[filterConfig.idField!].toString() === selectedIds[0]
                  )?.description;

                  subLabel =
                    selectedCount === 1
                      ? firstSelected || null
                      : `${firstSelected || ''} + ${selectedCount - 1} more`;

                  variant = 'contained';
                }
              }
            }

            const isActive = (selectedLabel === label && anchorEl) || Boolean(subLabel);

            return (
              <Button
                key={label}
                variant={variant}
                size="small"
                onClick={(e) => handleClick(e, label)}
                sx={{
                  textTransform: 'none',
                  mr: 1,
                  mb: 1,
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  borderRadius: 1,
                  transition: 'all 0.25s ease',

                  backgroundColor: isActive ? '#FFA100' : '#ffffff',
                  color: isActive ? '#000' : '#1e293b',
                  border: isActive ? '1px solid #FFA100' : '1px solid #e5e7eb',

                  '& span': {
                    color: isActive ? '#000' : '#64748b',
                  },



                  // 🟡 Hover = full yellow
                  '&:hover': {
                    backgroundColor: '#FFA100',
                    borderColor: '#FFA100',
                    color: '#000',

                    '& span': {
                      color: '#000',
                    },
                  },
                }}
              >
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {displayLabel}
                </Box>

                {subLabel && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: '0.75rem',
                      lineHeight: 1,
                      transition: 'inherit',
                    }}
                  >
                    {subLabel}
                  </Box>
                )}
              </Button>





            );
          })}

          <Button
            variant="outlined"
            size="small"
            startIcon={<TuneIcon />}
            onClick={() => setDrawerOpen(true)}
            sx={{
              textTransform: 'none',
              mr: 1,
              mb: 1,
              borderRadius: 1,
              transition: 'all 0.25s ease',
              borderColor: '#0f172a',
              color: '#0f172a',
              '&:hover': {
                backgroundColor: '#facc15',
                borderColor: '#facc15',
                color: '#000',
              },
            }}
          >
            All Filters
          </Button>

          <Button
            variant="outlined"
            size="small"
            startIcon={<SortIcon />}
            onClick={handleSortClick}
            sx={{
              textTransform: 'none',
              mr: 1,
              mb: 1,
              borderRadius: 1,
              transition: 'all 0.25s ease',

              // 🌑 Selected state (sorting applied)
              backgroundColor: sortList.length > 0 ? '#0f172a' : '#ffffff',
              borderColor: sortList.length > 0 ? '#0f172a' : '#e5e7eb',
              color: sortList.length > 0 ? '#ffffff' : '#1e293b',

              '& .MuiTypography-root': {
                color: sortList.length > 0 ? '#f1f5f9' : '#64748b',
              },

              // 🟡 Hover state
              '&:hover': {
                backgroundColor: '#facc15',
                borderColor: '#facc15',
                color: '#000',

                '& .MuiTypography-root': {
                  color: '#000',
                },
              },
            }}
          >
            {sortList.length === 0 && 'Sort'}

            <Box display="flex" flexDirection="column" alignItems="flex-start">
              {sortList.length > 0 && (
                <>
                  <Typography variant="caption">
                    {sortList.length} sort{sortList.length > 1 ? 's' : ''} applied
                  </Typography>

                  <Typography variant="caption">
                    Sort by: {sortLabelMap[sortList[0].key] || sortList[0].key}
                    {sortList.length > 1 && ' +more'}
                  </Typography>
                </>
              )}
            </Box>
          </Button>




          <Button
            variant="outlined"
            size="small"
            color="error"
            sx={{ textTransform: 'none', mr: 1, mb: 1 }}
            startIcon={<DeleteOutlineIcon />}
            onClick={() => {
              setSortAnchorEl(null);
              setSortList([]); // ✅ clear sort indicator state
              navigate(url ?? '/vehicle-listing');
            }}
          >
            Clear All Filters
          </Button>



          <Menu
            anchorEl={sortAnchorEl}
            open={Boolean(sortAnchorEl)}
            onClose={handleSortClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
              sx: { width: 220, borderRadius: 2, boxShadow: 3 }
            }}
          >
            {[
              { key: 'askingPrice', label: 'Price' },
              { key: 'addDate', label: 'Added Date' },
              { key: 'mileage', label: 'Mileage' },
              { key: 'vehicleModel', label: 'Model' },
            ].map(({ key, label }) => {
              const sortIndex = sortList.findIndex((s) => s.key === key);
              const sortItem = sortList[sortIndex];
              const isActive = sortIndex !== -1;
              const DirectionIcon = sortItem?.direction === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon;


              return (
                <MenuItem key={key} onClick={() => handleSortChange(key)}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2">
                      {label}
                      {isActive && sortList.length > 1 && (
                        <strong style={{ marginLeft: 4 }}>({sortIndex + 1})</strong>
                      )}
                    </Typography>

                    {isActive ? (
                      <DirectionIcon fontSize="small" />
                    ) : (
                      // Empty space to align with other icons
                      <Box sx={{ width: 20 }} />
                    )}
                  </Stack>
                </MenuItem>
              );


            })}
          </Menu>



        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: {
              width: 250, // Fixed width for all dropdowns
              maxHeight: 300, // Scroll if too tall
              mt: 1,
              borderRadius: 2,
              boxShadow: 3,
              overflowY: 'auto',
            },
          }}
        >
          {activeFilter === 'Brand & Model' &&
            renderCheckboxFilterMenu('vehicleBrandIds', brandData, 'vehicleBrandId', 'description')}


          {activeFilter === 'Body Type' &&
            renderCheckboxFilterMenu('vehicleBodyTypeIds', bodyTypesData, 'vehicleBodyTypeId', 'description')}


          {activeFilter === 'Fuel Type' &&
            renderCheckboxFilterMenu('vehicleFuelTypeIds', fuelTypeData, 'vehicleFuelTypeId', 'description')}

          {activeFilter === 'Transmission' &&
            renderCheckboxFilterMenu('vehicleTransmissionTypeIds', TransmissionTypeData, 'vehicleTransmissionTypeId', 'description')}

          {activeFilter === 'Vehicle Status' &&
            renderCheckboxFilterMenu('statusId', [
              { statusId: 'VPB', description: 'Available' },
              { statusId: 'VSL', description: 'Sold' },
            ], 'statusId', 'description')}


          {activeFilter === 'Year' && (
            <Box px={3} py={2} width={'100%'}>
              <Typography gutterBottom fontWeight="bold">
                Select Year Range
              </Typography>

              <Slider
                value={yearRange}
                onChange={handleYearChange}
                valueLabelDisplay="auto"
                min={minYear}
                max={maxYear}
                marks={yearMarks}
                sx={{ mt: 3, mb: 2 }}
              />

              <Stack direction="row" spacing={1} justifyContent="flex-end" mt={2}>
                <Button size="small" onClick={clearYearFilter}>
                  Clear
                </Button>
                <Button size="small" variant="contained" onClick={applyYearFilter}>
                  Apply
                </Button>
              </Stack>
            </Box>
          )}


        </Menu>
        <RangeFilterMenu
          anchorEl={anchorEl}
          activeFilter={activeFilter ?? ''}
          filterKey="Car Price & Instalment"
          minParamKey="minAskingPrice"
          maxParamKey="maxAskingPrice"
          min={1000}
          max={9_999_999}
          marks={[
            { value: 1000, label: '$1k' },
            { value: 9_999_999, label: '9999999' },
          ]}
          label="Select Price Range ($)"
          onClose={handleClose}
        />


        <RangeFilterMenu
          anchorEl={anchorEl}
          activeFilter={activeFilter ?? ''}
          filterKey="Depreciation"
          minParamKey="minDepreciation"
          maxParamKey="maxDepreciation"
          min={1000}
          max={100_000}
          marks={[
            { value: 1000, label: '$1k' },
            { value: 25000, label: '$25k' },
            { value: 50000, label: '$50k' },
            { value: 100000, label: '$100k' },
          ]}
          label="Select Depreciation / y Range ($)"
          onClose={handleClose}
        />

        <RangeFilterMenu
          anchorEl={anchorEl}
          activeFilter={activeFilter ?? ''}
          filterKey="Mileage"
          minParamKey="minMileage"
          maxParamKey="maxMileage"
          min={0}
          max={300000}
          marks={[
            { value: 0, label: '0 km' },
            { value: 50000, label: '50k km' },
            { value: 100000, label: '100k km' },
            { value: 300000, label: '300k km' },
          ]}
          label="Select Mileage Range (km)"
          onClose={handleClose}
        />



        <RightDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          fuelTypeData={fuelTypeData}
          TransmissionTypeData={TransmissionTypeData}
          bodyTypesData={bodyTypesData}
          brandData={brandData}
          dealerData={dealerData}
        />
      </Box>

      {viewMode && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', px: 2 }}>
          <Tooltip title={`Switch to ${viewMode === 'grid' ? 'list' : 'grid'} view`}>
            <IconButton onClick={onToggleViewMode}>
              {viewMode === 'grid' ? <ViewListIcon /> : <ViewModuleIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      )}


    </>




  );
};


export default CarFilterBar;
